/* PesInbox — iter98.99ea · Shared pickup queue for the Patient Engagement
 * Specialist (and CoE-Lead + CoE-Support) roles.
 *
 * iter101.40 — Rebuilt to the canonical Admin command-center grammar so
 * PES/Sales/Support portals share the same vocabulary as `/admin/leads`:
 *   • CommandHeader (overline + h1 + subtitle + refresh pill)
 *   • 6-tile AdminKpiTile strip in the same shape as Lead Command Center
 *   • AdminSegmentedTabs for kind switching (All / Web / B2B / Missed / Overdue)
 *   • FilterSearchBar (search + sort dropdown in the right slot)
 *   • Table row grammar preserved — it's the working surface, not the shell.
 *
 * Data: GET /api/coe/leads-inbox → {rows, counts, auto_released_this_fetch}
 *       POST /api/coe/leads-inbox/{kind}/{lead_id}/claim
 *
 * Anti-cherry-pick: backend already sorts breached-SLA rows first + oldest
 * first; the "Urgency" sort preserves that; "Hottest"/"Newest" are triage
 * overrides.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Tray, Globe, Buildings, Flame, ArrowsClockwise,
    Warning, Clock, Phone, ArrowRight, Sparkle, CaretDown,
    ArrowUUpLeft, UserList,
} from "@phosphor-icons/react";
import TeamActivityFeed from "@/pages/coe/TeamActivityFeed";
import { AdminKpiTile, CommandHeader } from "@/components/admin/AdminKpiTile";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";
import { FilterSearchBar } from "@/components/doctor/FilterBar";


const KIND_META = {
    web: { label: "Web lead",     short: "Web", Icon: Globe,     cls: "bg-cyan-100 text-cyan-700" },
    b2b: { label: "B2B / Clinic", short: "B2B", Icon: Buildings, cls: "bg-violet-100 text-violet-700" },
};


const TABS = [
    { id: "all",             label: "All",             Icon: UserList },
    { id: "web",             label: "Web",             Icon: Globe },
    { id: "b2b",             label: "B2B",             Icon: Buildings },
    { id: "missed_callback", label: "Missed callback", Icon: Clock },
    { id: "overdue",         label: "Overdue",         Icon: Warning },
];


function relTime(iso) {
    if (!iso) return "—";
    try {
        const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
        if (days >= 1) return `${days}d ago`;
        const hrs = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
        if (hrs >= 1) return `${hrs}h ago`;
        const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
        return `${mins}m ago`;
    } catch { return "—"; }
}


function slaPill(sla) {
    if (!sla || sla.state === "not_assigned") return { label: "No SLA yet", cls: "bg-slate-100 text-slate-700", tone: "neutral" };
    const remaining = (sla.hours_target ?? 0) - (sla.elapsed_hours ?? 0);
    if (sla.state === "breached") {
        return { label: `${Math.abs(remaining).toFixed(1)}h overdue`, cls: "bg-rose-100 text-rose-700 animate-pulse", tone: "breach" };
    }
    if (sla.state === "danger" || sla.state === "warn") {
        return { label: `${remaining.toFixed(1)}h left`, cls: "bg-amber-100 text-amber-800", tone: "warn" };
    }
    return { label: `${remaining.toFixed(1)}h left`, cls: "bg-emerald-100 text-emerald-700", tone: "ok" };
}


export default function PesInbox() {
    const [data, setData] = useState({ rows: [], counts: {}, auto_released_this_fetch: 0, auto_release_hours: 24 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [sortKey, setSortKey] = useState("urgency");
    const [claiming, setClaiming] = useState(null);
    const [feedKey, setFeedKey] = useState(0);
    const navigate = useNavigate();

    const load = async (showReleaseToast = false) => {
        setLoading(true);
        try {
            const r = await api.get("/coe/leads-inbox");
            setData(r.data || { rows: [], counts: {} });
            if (showReleaseToast && r.data?.auto_released_this_fetch > 0) {
                toast.info(`${r.data.auto_released_this_fetch} stale claim${r.data.auto_released_this_fetch === 1 ? "" : "s"} released back to the inbox.`);
            }
            setFeedKey((k) => k + 1);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't load the inbox");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(true); }, []);

    const visible = useMemo(() => {
        let rows = data.rows || [];
        if (filter === "overdue")             rows = rows.filter((r) => r.sla?.state === "breached");
        else if (filter === "missed_callback") rows = rows.filter((r) => r.missed_callback === true);
        else if (filter !== "all")             rows = rows.filter((r) => r.kind === filter);
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            rows = rows.filter((r) =>
                (r.name || "").toLowerCase().includes(q)
                || (r.phone || "").toLowerCase().includes(q)
                || (r.city || "").toLowerCase().includes(q)
                || (r.source || "").toLowerCase().includes(q)
                || (r.code || "").toLowerCase().includes(q),
            );
        }
        if (sortKey === "hottest") {
            rows = [...rows].sort((a, b) => (b.score || 0) - (a.score || 0));
        } else if (sortKey === "newest") {
            rows = [...rows].sort((a, b) => {
                const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
                const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
                return tb - ta;
            });
        }
        return rows;
    }, [data.rows, filter, query, sortKey]);

    const handleClaim = async (row) => {
        setClaiming({ kind: row.kind, id: row.id });
        try {
            const r = await api.post(`/coe/leads-inbox/${row.kind}/${row.id}/claim`);
            toast.success(`Claimed · ${row.name}`);
            setFeedKey((k) => k + 1);
            navigate(r.data?.detail_link || row.detail_link);
        } catch (err) {
            if (err.response?.status === 409) {
                toast.error(err.response.data.detail || "Just claimed by another specialist");
                await load(false);
            } else {
                toast.error(err.response?.data?.detail || "Couldn't claim");
            }
        } finally {
            setClaiming(null);
        }
    };

    const counts = data.counts || {};

    return (
        <div className="space-y-6" data-testid="pes-inbox-page">
            <CommandHeader
                overline="Patient Engagement · Inbox"
                title="Shared Inbox"
                subtitle={`Available leads to claim. Inactive claims auto-release back here after ${data.auto_release_hours || 24}h.`}
                testid="pes-inbox-header"
                right={
                    <button
                        type="button"
                        onClick={() => load(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-sm hover:bg-secondary/40 transition"
                        data-testid="pes-inbox-refresh"
                    >
                        <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                }
            />

            {/* 6-tile KPI strip · same footprint as /admin/leads. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="pes-inbox-stats">
                <AdminKpiTile label="Unclaimed"       value={counts.total ?? 0}           tone="primary" Icon={Tray}      active={filter === "all"}             onClick={() => setFilter("all")}             testid="pes-inbox-stat-total" />
                <AdminKpiTile label="Overdue"         value={counts.overdue ?? 0}         tone="rose"    Icon={Warning}   active={filter === "overdue"}         onClick={() => setFilter("overdue")}         testid="pes-inbox-stat-overdue" />
                <AdminKpiTile label="Missed callback" value={counts.missed_callback ?? 0} tone="rose"    Icon={Clock}     active={filter === "missed_callback"} onClick={() => setFilter("missed_callback")} testid="pes-inbox-stat-missed-callback" />
                <AdminKpiTile label="Web"             value={counts.web ?? 0}             tone="cyan"    Icon={Globe}     active={filter === "web"}             onClick={() => setFilter("web")}             testid="pes-inbox-stat-web" />
                <AdminKpiTile label="B2B"             value={counts.b2b ?? 0}             tone="violet"  Icon={Buildings} active={filter === "b2b"}             onClick={() => setFilter("b2b")}             testid="pes-inbox-stat-b2b" />
                <AdminKpiTile label="Auto-released"   value={data.auto_released_this_fetch ?? 0} sub={`in last ${data.auto_release_hours ?? 24}h`} tone="amber" Icon={ArrowUUpLeft} testid="pes-inbox-stat-auto-released" />
            </div>

            <AdminSegmentedTabs
                tabs={TABS.map((t) => ({
                    id: t.id,
                    label: t.label,
                    Icon: t.Icon,
                    count: t.id === "all"
                        ? (counts.total ?? 0)
                        : t.id === "overdue"
                            ? (counts.overdue ?? 0)
                            : t.id === "missed_callback"
                                ? (counts.missed_callback ?? 0)
                                : (counts[t.id] ?? 0),
                }))}
                active={filter}
                onChange={setFilter}
                testid="pes-inbox-tabs"
                tabTestidBase="pes-inbox-tab"
                showZero
            />

            <FilterSearchBar
                query={query}
                setQuery={setQuery}
                placeholder="Search name, phone, city, or Lead ID…"
                searchTestid="pes-inbox-search"
                testid="pes-inbox-search-bar"
                rightSlot={
                    <div className="relative">
                        <select
                            value={sortKey}
                            onChange={(e) => setSortKey(e.target.value)}
                            className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                            data-testid="pes-inbox-sort-select"
                            aria-label="Sort inbox"
                        >
                            <option value="urgency">Urgency (default)</option>
                            <option value="hottest">Hottest first</option>
                            <option value="newest">Newest first</option>
                        </select>
                        <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                    </div>
                }
            />

            {/* Table */}
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-sm text-muted-foreground">Loading inbox…</div>
                ) : visible.length === 0 ? (
                    <EmptyState filter={filter} totalUnclaimed={counts.total ?? 0} />
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/30 border-b border-border">
                            <tr className="text-left text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                                <th className="px-5 py-3">Lead</th>
                                <th className="px-3 py-3">Kind</th>
                                <th className="px-3 py-3">Source</th>
                                <th className="px-3 py-3">Score</th>
                                <th className="px-3 py-3">SLA</th>
                                <th className="px-3 py-3 text-right">Age</th>
                                <th className="px-3 py-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((row) => (
                                <InboxRow
                                    key={`${row.kind}-${row.id}`}
                                    row={row}
                                    onClaim={() => handleClaim(row)}
                                    claiming={claiming?.kind === row.kind && claiming?.id === row.id}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <TeamActivityFeed refreshKey={feedKey} />
        </div>
    );
}


function InboxRow({ row, onClaim, claiming }) {
    const meta = KIND_META[row.kind] || KIND_META.web;
    const KIcon = meta.Icon;
    const pill = slaPill(row.sla);
    const isHot = (row.score || 0) >= 70;
    return (
        <tr
            className={`border-b border-border last:border-0 transition ${
                row.missed_callback
                    ? "bg-rose-50/40"
                    : pill.tone === "breach"
                        ? "bg-rose-50/30"
                        : pill.tone === "warn"
                            ? "bg-amber-50/20"
                            : ""
            }`}
            data-testid={`pes-inbox-row-${row.id}`}
        >
            <td className="px-5 py-3">
                <div className="font-medium flex items-center gap-1.5 flex-wrap">
                    {row.name}
                    {row.previously_claimed && (
                        <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200"
                            title={`Auto-released ${row.release_count}× before — someone tried this and walked away.`}
                            data-testid={`pes-inbox-returned-${row.id}`}
                        >
                            <ArrowUUpLeft size={9} weight="bold" />
                            Returned{row.release_count > 1 ? ` ×${row.release_count}` : ""}
                        </span>
                    )}
                    {row.missed_callback && (
                        <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 border border-rose-200"
                            title={row.preferred_slot ? `Caller asked for ${row.preferred_slot} — window passed` : "Missed call slot"}
                            data-testid={`pes-inbox-missed-${row.id}`}
                        >
                            <Warning size={9} weight="fill" />
                            Missed callback
                        </span>
                    )}
                </div>
                {row.code && (
                    <div className="text-[10px] font-mono text-muted-foreground/70 mt-0.5" data-testid={`pes-inbox-code-${row.id}`}>
                        {row.code}
                    </div>
                )}
                <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                    <Phone size={10} /> {row.phone}
                </div>
            </td>
            <td className="px-3 py-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${meta.cls}`} data-testid={`pes-inbox-kind-${row.kind}-${row.id}`}>
                    <KIcon size={10} weight="fill" /> {meta.short}
                </span>
            </td>
            <td className="px-3 py-3 text-[12.5px] text-muted-foreground">{row.source}</td>
            <td className="px-3 py-3">
                {isHot ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700">
                        <Flame size={10} weight="fill" /> {row.score}
                    </span>
                ) : (
                    <span className="text-[12px] text-muted-foreground tabular-nums">{row.score || "—"}</span>
                )}
            </td>
            <td className="px-3 py-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${pill.cls}`} data-testid={`pes-inbox-sla-${row.id}`}>
                    <Clock size={10} /> {pill.label}
                </span>
            </td>
            <td className="px-3 py-3 text-right text-[11.5px] text-muted-foreground whitespace-nowrap">
                {relTime(row.created_at)}
            </td>
            <td className="px-3 py-3 text-right">
                <button
                    type="button"
                    onClick={onClaim}
                    disabled={claiming}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-bold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap uppercase tracking-wider"
                    data-testid={`pes-inbox-claim-${row.id}`}
                >
                    {claiming ? "…" : "Claim"} <ArrowRight size={11} weight="bold" />
                </button>
            </td>
        </tr>
    );
}


function EmptyState({ filter, totalUnclaimed }) {
    const text = filter === "all"
        ? (totalUnclaimed === 0
            ? "Everything's claimed. Nice work — admin will route new leads here as they come in."
            : "No matches for your search. Try clearing it.")
        : `No ${filter.replace("_", " ")} leads waiting right now.`;
    return (
        <div className="p-12 text-center" data-testid="pes-inbox-empty">
            <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 grid place-items-center mb-3">
                <Sparkle size={24} className="text-primary" weight="duotone" />
            </div>
            <div className="font-display text-base font-semibold">All clear</div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto leading-relaxed">{text}</p>
        </div>
    );
}
